import type { AppState, CatalogueItem, FocusSession, Rarity } from '../types'

export const ECONOMY_VERSION = 'mvp-1'
export const DISCOVERY_MARKS_PER_TICKET = 8
export const GUARANTEE_THRESHOLD = 5

export function calculateFeed(minutes: number): number {
  if (minutes < 10) return 0
  return Math.min(12, Math.floor(minutes / 5) + (minutes >= 20 ? 1 : 0))
}

export function calculateMarks(minutes: number): number {
  if (minutes < 10) return 0
  return Math.min(6, Math.floor(minutes / 10))
}

export function rewardSession(state: AppState, session: FocusSession): AppState {
  if (session.rewarded || session.status !== 'completed') return state
  const feedEarned = calculateFeed(session.plannedMinutes)
  const marksEarned = calculateMarks(session.plannedMinutes)
  const combinedMarks = state.discoveryMarks + marksEarned
  const earnedTickets = Math.floor(combinedMarks / DISCOVERY_MARKS_PER_TICKET)
  const rewardedSession = { ...session, rewarded: true, feedEarned, marksEarned }

  return {
    ...state,
    feed: state.feed + feedEarned,
    discoveryMarks: combinedMarks % DISCOVERY_MARKS_PER_TICKET,
    tickets: state.tickets + earnedTickets,
    sessions: state.sessions.map((candidate) => (candidate.id === session.id ? rewardedSession : candidate)),
  }
}

export function fishStage(feedTotal: number): 1 | 2 | 3 {
  if (feedTotal >= 70) return 3
  if (feedTotal >= 20) return 2
  return 1
}

export function feedNeeded(feedTotal: number): number {
  if (feedTotal < 20) return 20 - feedTotal
  if (feedTotal < 70) return 70 - feedTotal
  return 0
}

export function duplicateFeed(rarity: Rarity): number {
  return { common: 8, uncommon: 15, rare: 30, special: 50 }[rarity]
}

function hashNumber(value: string): number {
  let hash = 2166136261
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}

export interface DrawResult {
  item: CatalogueItem
  duplicate: boolean
  conversionFeed: number
  nextState: AppState
}

export function drawDiscovery(state: AppState, catalogue: CatalogueItem[], drawId: string): DrawResult {
  if (state.tickets < 1) throw new Error('A Discovery Ticket is required.')
  const pool = catalogue.filter((item) => item.unlock === 'discovery')
  const owned = new Set(state.inventory.map((entry) => entry.itemId))
  const unseen = pool.filter((item) => !owned.has(item.id))
  const mustBeNew = state.spins < 5 || state.guarantee >= GUARANTEE_THRESHOLD
  const eligible = mustBeNew && unseen.length ? unseen : pool
  const item = eligible[hashNumber(`${drawId}:${ECONOMY_VERSION}`) % eligible.length]
  const duplicate = owned.has(item.id)
  const conversionFeed = duplicate ? duplicateFeed(item.rarity) : 0
  const inventory = duplicate
    ? state.inventory
    : [...state.inventory, { itemId: item.id, obtainedAt: Date.now(), source: 'discovery' as const }]

  return {
    item,
    duplicate,
    conversionFeed,
    nextState: {
      ...state,
      tickets: state.tickets - 1,
      spins: state.spins + 1,
      feed: state.feed + conversionFeed,
      guarantee: duplicate ? Math.min(GUARANTEE_THRESHOLD, state.guarantee + 1) : 0,
      inventory,
    },
  }
}
